import {
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
  Switch,
} from 'antd';
import { FormInstance } from 'antd/es/form/Form';
import { FormattedMessage, useIntl } from 'react-intl';
import FloatingFormItem from '../../FloatingFormItem';
import InputDate from '../../InputDate';
import PhoneFormField from '../../PhoneFormField';
import {
  ContactType,
  contactTypeOptions,
  contactTypeTranslate,
} from '@/core/domain/_shared/ContactType';
import { LegalPersonContactFormInterface } from '../types';
import {
  useDefaultEmptyFieldError,
  useDefaultMaxLengthFieldError,
  useDefaultMinLengthFieldError,
} from '@/hooks';
import { Trash2 } from 'react-feather';
import { useState } from 'react';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { ContactDetailList } from '@/core/domain/people/_shared/Contact';

interface Props {
  personType: 'NaturalPerson' | 'LegalPerson';
  form: FormInstance<LegalPersonContactFormInterface>;
  onDetailRemove?(detailId: number): Promise<void>;
  fieldsDisabled?: boolean;
}

type TranslateTypes = 'contactList.' | 'contactList.contactType.' | '';

export default function PersonContactForm({
  form,
  onDetailRemove,
  personType,
  fieldsDisabled,
}: Props) {
  const intl = useIntl().formatMessage;
  const [detailRemoveLoading, setDetailRemoveLoading] = useState('');

  const translate = (message: string, type: TranslateTypes = '') =>
    intl({ id: `commons.modal.contact.form.${type}${message}` });

  const requiredRule = useDefaultEmptyFieldError();
  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();
  const defaultMinLenghtFieldError = useDefaultMinLengthFieldError();

  const genderOptions = [
    {
      value: 'MALE',
      label: intl({ id: 'commons.modal.contact.form.gender.male' }),
    },
    {
      value: 'FEMALE',
      label: intl({ id: 'commons.modal.contact.form.gender.female' }),
    },
  ];

  const handleMainContactChange = (
    checked: boolean,
    index: number,
    contactType: ContactType,
  ) => {
    if (checked) {
      const detailList: ContactDetailList[] = form.getFieldValue('detailList');
      const updatedDetailList = detailList?.map((detail, i) => ({
        ...detail,
        main:
          (i === index && detail.contactType === contactType) ||
          (detail.contactType === contactType ? false : detail.main),
      }));
      form.setFieldsValue({ detailList: updatedDetailList });
    }
  };

  const resetMainContactOnTypeUpdate = (index: number) => {
    const detailList: ContactDetailList[] = form.getFieldValue('detailList');
    const updatedDetailList = detailList?.map((detail, i) => ({
      ...detail,
      main: i === index ? false : detail.main,
    }));
    form.setFieldsValue({ detailList: updatedDetailList });
  };

  return (
    <Form layout="vertical" form={form}>
      <Form.Item hidden name="contactId" />

      <Row gutter={[12, 12]} align="middle">
        <>
          <Col xs={24} sm={24} md={personType === 'LegalPerson' ? 14 : 24}>
            <FloatingFormItem
              name="name"
              label={translate('name')}
              rules={[requiredRule, defaultMaxLenghtFieldError(255)]}
            >
              <Input disabled={fieldsDisabled} />
            </FloatingFormItem>
          </Col>

          {personType === 'LegalPerson' && (
            <Col xs={24} sm={12} md={10}>
              <FloatingFormItem
                name="department"
                label={translate('department')}
                rules={[defaultMaxLenghtFieldError(100)]}
              >
                <Input disabled={fieldsDisabled} />
              </FloatingFormItem>
            </Col>
          )}

          <Col xs={24} sm={12}>
            <FloatingFormItem
              name="gender"
              label={translate('gender')}
              initialValue={'MALE'}
            >
              <Select options={genderOptions} disabled={fieldsDisabled} />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={12}>
            <FloatingFormItem name="birthDate" label={translate('birthDate')}>
              <InputDate disabled={fieldsDisabled} />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={12}>
            <Form.Item name="main" initialValue={false} valuePropName="checked">
              <Checkbox style={{ marginTop: '12px' }} disabled={fieldsDisabled}>
                Grupo de contato principal?
              </Checkbox>
            </Form.Item>
          </Col>
        </>

        <Col xs={24} sm={24} md={24}>
          <h2 style={{ marginBottom: 12 }}>Detalhes do contato</h2>

          <Form.List name="detailList" initialValue={[]}>
            {(detailList, operation) => {
              return (
                <>
                  {detailList.map((detailItem) => {
                    const detailId = form.getFieldValue([
                      'detailList',
                      detailItem.name,
                      'id',
                    ]);

                    const removeDetailItem = async () => {
                      if (detailId) {
                        setDetailRemoveLoading(detailId);
                        if (onDetailRemove) await onDetailRemove(detailId);
                      } else {
                        operation.remove(detailItem.name);
                      }

                      setDetailRemoveLoading('');
                    };

                    return (
                      <div key={detailItem.key}>
                        <Space
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Space align="center">
                            <big style={{ fontWeight: 'bold' }}>
                              Contato {detailItem.name + 1}
                            </big>
                            <Form.Item hidden name={[detailItem.name, 'id']} />

                            <Form.Item
                              name={[detailItem.name, 'active']}
                              noStyle
                              valuePropName="checked"
                            >
                              <Switch
                                checkedChildren="Ativo"
                                unCheckedChildren="Inativo"
                                disabled={fieldsDisabled}
                              />
                            </Form.Item>
                          </Space>

                          <Button
                            onClick={removeDetailItem}
                            icon={<Trash2 width={16} />}
                            loading={detailRemoveLoading === detailId}
                            disabled={
                              !hasPermissions([
                                Authorities.REGISTER_CUSTOMER_DELETE,
                              ]) || fieldsDisabled
                            }
                          />
                        </Space>

                        <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
                          <Col xs={24} sm={12} md={12}>
                            <FloatingFormItem
                              parentFieldName="detailList"
                              name={[detailItem.name, 'contactType']}
                              fieldKey={[detailItem.key, 'contactType']}
                              label={translate('contactType', 'contactList.')}
                            >
                              <Select
                                options={contactTypeOptions}
                                onChange={() =>
                                  resetMainContactOnTypeUpdate(detailItem.name)
                                }
                                disabled={fieldsDisabled}
                              />
                            </FloatingFormItem>
                          </Col>

                          <Col xs={24} sm={12} md={12}>
                            <Form.Item noStyle shouldUpdate>
                              {({ getFieldValue }) => {
                                const contactType: string =
                                  getFieldValue([
                                    'detailList',
                                    detailItem.key,
                                    'contactType',
                                  ]) ?? ContactType.Mobile;

                                if (contactType === ContactType.Email) {
                                  return (
                                    <FloatingFormItem
                                      parentFieldName="detailList"
                                      name={[detailItem.name, 'value']}
                                      fieldKey={[detailItem.key, 'value']}
                                      label={
                                        <FormattedMessage id="commons.contact.contactType.email" />
                                      }
                                      rules={[
                                        {
                                          type: 'email',
                                          message: intl({
                                            id: 'commons.form.errors.invalidEmail',
                                          }),
                                        },
                                        requiredRule,
                                        defaultMaxLenghtFieldError(100),
                                      ]}
                                    >
                                      <Input disabled={fieldsDisabled} />
                                    </FloatingFormItem>
                                  );
                                } else if (
                                  contactType === ContactType.Foreign
                                ) {
                                  return (
                                    <FloatingFormItem
                                      parentFieldName="detailList"
                                      name={[detailItem.name, 'value']}
                                      fieldKey={[detailItem.key, 'value']}
                                      label={
                                        <FormattedMessage id="commons.contact.contactType.foreign" />
                                      }
                                      rules={[
                                        requiredRule,
                                        defaultMaxLenghtFieldError(15),
                                        defaultMinLenghtFieldError(10),
                                      ]}
                                    >
                                      <Input disabled={fieldsDisabled} />
                                    </FloatingFormItem>
                                  );
                                } else {
                                  return (
                                    <PhoneFormField
                                      parentFieldName="detailList"
                                      name={[detailItem.name, 'value']}
                                      fieldKey={[detailItem.key, 'value']}
                                      asPhone={
                                        contactType === ContactType.Phone
                                      }
                                      label={
                                        <FormattedMessage
                                          id={`commons.contact.contactType.${contactType.toLowerCase()}`}
                                        />
                                      }
                                      disabled={fieldsDisabled}
                                    />
                                  );
                                }
                              }}
                            </Form.Item>
                          </Col>

                          <Col span={24}>
                            <FloatingFormItem
                              parentFieldName="detailList"
                              name={[detailItem.name, 'description']}
                              fieldKey={[detailItem.key, 'description']}
                              label={translate('description', 'contactList.')}
                              rules={[defaultMaxLenghtFieldError(100)]}
                            >
                              <Input disabled={fieldsDisabled} />
                            </FloatingFormItem>
                          </Col>

                          <Col span={24}>
                            <Form.Item noStyle shouldUpdate>
                              {({ getFieldValue }) => {
                                const currentContactType = getFieldValue([
                                  'detailList',
                                  detailItem.name,
                                  'contactType',
                                ]) as ContactType;
                                const isMain = getFieldValue([
                                  'detailList',
                                  detailItem.name,
                                  'main',
                                ]);

                                return (
                                  <Form.Item
                                    name={[detailItem.name, 'main']}
                                    initialValue={false}
                                    valuePropName="checked"
                                  >
                                    <Checkbox
                                      style={{ marginTop: '12px' }}
                                      checked={isMain}
                                      onChange={(event) =>
                                        handleMainContactChange(
                                          event.target.checked,
                                          detailItem.name,
                                          currentContactType,
                                        )
                                      }
                                      disabled={fieldsDisabled}
                                    >
                                      {`${contactTypeTranslate[currentContactType]} principal?`}
                                    </Checkbox>
                                  </Form.Item>
                                );
                              }}
                            </Form.Item>
                          </Col>
                        </Row>

                        <Divider style={{ marginTop: 12 }} />
                      </div>
                    );
                  })}

                  <Button
                    onClick={() =>
                      operation.add({
                        description: '',
                        contactType: ContactType.Mobile,
                        value: '',
                        active: true,
                        main: false,
                      })
                    }
                    block
                    disabled={fieldsDisabled}
                  >
                    Adicionar contato
                  </Button>
                </>
              );
            }}
          </Form.List>
        </Col>
      </Row>
    </Form>
  );
}
